import {
    ATTRIBUTES,
    SELECTORS,
    getUrlWithPreviewThemeId,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { shopifySectionAPI } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ProductInfo } from '@/scripts/product/product-info'
import { SizePopupDialogue } from '@/scripts/product/size-popup-dialogue'
import {
	resolveStoqVariantState,
	syncResolvedStoqVariantSelection,
} from '@/scripts/product/stoq-selling-plan'
import {
	clearScheduledSubmitButtonLabelSync,
	scheduleSubmitButtonLabelSync,
	type SubmitButtonState,
} from '@/scripts/product/submit-button-label-sync'
import { type ProductStickyAtc } from '@/scripts/product/product-sticky-atc'
import { type VariantRadios } from '@/scripts/theme/variant-radios'
import { type VariantSelects } from '@/scripts/theme/variant-selects'
import {
	replaceAll,
} from '@/scripts/core/global'

function nextPaint(callback: () => void) {
	requestAnimationFrame(() => {
		requestAnimationFrame(callback)
	})
}

export class ProductVariantPicker extends UcoastEl {
    static htmlSelector = 'product-variant-picker'
    submitButton?: HTMLButtonElement
    formIdEl?: HTMLInputElement
    form?: HTMLFormElement

    constructor() {
        super()

        const firstSizeInput = q.os<HTMLInputElement>('.size-radios__size-input', this)
        const stickyAtc = this.closest('product-sticky-atc') as HTMLElement | null
        const formId = firstSizeInput?.getAttribute('form')
        this.form = (formId
            ? q.rs<HTMLFormElement>(`#${CSS.escape(formId)}`, document)
            : q.os<HTMLFormElement>('form', stickyAtc ?? undefined) ||
              q.os<HTMLFormElement>('form', document)) as HTMLFormElement | undefined

        if (this.form) {
            this.submitButton = (
                q.os<HTMLButtonElement>('.product-form__submit', this.form) ||
                q.os<HTMLButtonElement>('.product-sticky-atc__submit-button', stickyAtc ?? this.form)
            ) as HTMLButtonElement | undefined
            this.formIdEl =
                (this.form.querySelector('[name="id"]') ||
                    this.form.querySelector('[name="items[0][id]"]')) as HTMLInputElement | undefined
        }

        this.querySelectorAll('.variant-picker__color-item').forEach(swatch => {
            swatch.addEventListener('click', this.onChangeColor.bind(this))
        })
        this.querySelectorAll('.size-radios__gender-input').forEach(input => {
            input.addEventListener('change', this.onChangeGender.bind(this))
        })
        this.querySelectorAll('.size-radios__size-input').forEach(input => {
            input.addEventListener('change', this.onChangeSize.bind(this))
        })
		if (firstSizeInput && this.submitButton) {
			scheduleSubmitButtonLabelSync(
				this.submitButton,
				() => this.getSubmitButtonState()
			)
			nextPaint(() => {
				this.syncSelectedSizeState()
			})
		}
    }

	private syncSelectedSizeState() {
		if (!this.form || !this.formIdEl || !this.submitButton) return

		const selectedSizeInput = q.os<HTMLInputElement>(
			'.size-radios__size-input:checked',
			this
		)
		if (!selectedSizeInput) return

		const resolvedState = resolveStoqVariantState({
			variantId: selectedSizeInput.value,
			available: selectedSizeInput.dataset.available == 'true',
			preorder: selectedSizeInput.dataset.preorder == 'true',
			inventoryStatus: selectedSizeInput.dataset.inventoryStatus || '',
			inventoryStatusOverride:
				selectedSizeInput.dataset.inventoryStatusOverride || '',
			inventoryStatusText: selectedSizeInput.dataset.inventoryStatusText || '',
			liquidSellingPlanId: selectedSizeInput.dataset.sellingPlanId || '',
		})

		this.formIdEl.value = selectedSizeInput.value

		syncResolvedStoqVariantSelection({
			form: this.form,
			submitButton: this.submitButton,
			variantId: selectedSizeInput.value,
			resolvedState,
			fallbackShippingText: selectedSizeInput.dataset.shippingText || '',
			oosPopupUrl: `${this.dataset.url}?variant=${selectedSizeInput.value}&section_id=waitlist`,
		})
		scheduleSubmitButtonLabelSync(
			this.submitButton,
			() => this.getSubmitButtonState()
		)
	}

	private getSubmitButtonState(): SubmitButtonState {
		if (!this.submitButton) {
			return {
				available: false,
				preorder: false,
				inventoryStatus: 'OutOfStock',
				inventoryStatusText: '',
				priceInCents: null,
			}
		}

		const selectedSizeInput = q.os<HTMLInputElement>(
			'.size-radios__size-input:checked',
			this
		)
		const fallbackLabel =
			q.os<HTMLElement>('[data-uc-inventory-status-text]', this.submitButton)
				?.textContent?.trim() || ''

		return {
			...resolveStoqVariantState({
				variantId: selectedSizeInput?.value,
				available: selectedSizeInput?.dataset.available !== 'false',
				preorder: selectedSizeInput?.dataset.preorder === 'true',
				inventoryStatus: selectedSizeInput?.dataset.inventoryStatus || '',
				inventoryStatusOverride:
					selectedSizeInput?.dataset.inventoryStatusOverride || '',
				inventoryStatusText:
					selectedSizeInput?.dataset.inventoryStatusText || fallbackLabel,
				liquidSellingPlanId: selectedSizeInput?.dataset.sellingPlanId || '',
			}),
			priceInCents: selectedSizeInput?.dataset.price || null,
		}
	}

    async onChangeColor(e: Event) {
        e.preventDefault()
        const target = e.currentTarget as HTMLButtonElement
        if(!target) return
        const productUrl = target.dataset.url
        if(!productUrl) return
        const originalElement = target.closest('section[id^="MainProduct-"]') as HTMLElement
        if(!originalElement) return

        // Reset size popup so it can show again after color change
        SizePopupDialogue.findForSection(originalElement)?.reset()

        const currentGenderInput = q.os<HTMLInputElement>('.size-radios__gender-input:checked', this)
        const currentSizeInput = q.os<HTMLInputElement>('.size-radios__size-input:checked', this)
        const savedGender = currentGenderInput?.value || null
        const savedVariantId = currentSizeInput?.value || null
        
        // Get the size display name for fallback matching
        let savedSizeDisplayName: string | null = null
        if (currentSizeInput) {
            const sizeLabel = q.os<HTMLLabelElement>(`label[for="${currentSizeInput.id}"]`, this)
            if (sizeLabel) {
                // First try to get gender-specific text if gender is selected
                if (savedGender) {
                    const genderSpecificText = q.os<HTMLElement>(`.size-radios__size-text[data-gender="${savedGender}"]:not(.hidden)`, sizeLabel)
                    if (genderSpecificText) {
                        savedSizeDisplayName = genderSpecificText.textContent?.trim() || null
                    }
                }
                // Fallback to any visible size text
                if (!savedSizeDisplayName) {
                    const visibleSizeText = q.os<HTMLElement>('.size-radios__size-text:not(.hidden)', sizeLabel)
                    if (visibleSizeText) {
                        savedSizeDisplayName = visibleSizeText.textContent?.trim() || null
                    }
                }
                // Last resort: get any size text
                if (!savedSizeDisplayName) {
                    const anySizeText = q.os<HTMLElement>('.size-radios__size-text', sizeLabel)
                    if (anySizeText) {
                        savedSizeDisplayName = anySizeText.textContent?.trim() || null
                    }
                }
            }
        }
        
        const savedDefaultVariantOptions = this.saveDefaultVariantOptions(originalElement)
        
        const stickyAtc = q.os<ProductStickyAtc>('product-sticky-atc', document)
        let savedStickyAtcGender: string | null = null
        let savedStickyAtcVariantId: string | null = null
        let savedStickyAtcSizeDisplayName: string | null = null
        if (stickyAtc) {
            const stickyGenderInput = q.os<HTMLInputElement>('.product-sticky-atc-gender-input:checked', stickyAtc)
            savedStickyAtcGender = stickyGenderInput?.value || null
            
            // Only save size if ATC is enabled (meaning a size was explicitly selected)
            const isAtcEnabled = !stickyAtc.submitButton.hasAttribute('aria-disabled') || stickyAtc.submitButton.getAttribute('aria-disabled') !== 'true'
            if (isAtcEnabled) {
                const stickyVariantButton = q.os<HTMLButtonElement>('.product-sticky-atc__variant-button.active', stickyAtc)
                savedStickyAtcVariantId = stickyVariantButton?.dataset.id || null
                savedStickyAtcSizeDisplayName = stickyVariantButton?.dataset.value || null
            }
        }
        
        target.setAttribute('aria-disabled', 'true')
        target.setAttribute(ATTRIBUTES.loading, '')
        this.getSpinner(target).classList.remove('hidden')

		// Synchronize fade across all sections
		const sectionNodeList = document.querySelectorAll('.content-for-layout section.shopify-section')
		const sectionEls = Array.from(sectionNodeList) as HTMLElement[]
		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

		// Build fade targets excluding sticky ATC so it remains visible as an anchor
		const fadeTargets: HTMLElement[] = []
		sectionEls.forEach((sectionEl) => {
			const sticky = sectionEl.querySelector('product-sticky-atc') as HTMLElement | null
			if (sticky) {
				Array.from(sectionEl.children).forEach((child) => {
					if (child !== sticky) fadeTargets.push(child as HTMLElement)
				})
			} else {
				fadeTargets.push(sectionEl)
			}
		})

		const previousStyles = new Map<HTMLElement, { transition: string; opacity: string }>()
		let fadeOut: Promise<void> = Promise.resolve()
		const DURATION = 160
		if (!prefersReducedMotion) {
			for (const el of fadeTargets) {
				previousStyles.set(el, { transition: el.style.transition, opacity: el.style.opacity })
				el.style.transition = `opacity ${DURATION}ms ease`
				el.style.opacity = '0'
			}
			fadeOut = new Promise((resolve) => setTimeout(resolve, DURATION))
		}

		type SectionPayload =
			| { type: 'main'; sectionEl: HTMLElement; productElement: HTMLElement }
			| { type: 'section'; sectionEl: HTMLElement; htmlString: string }

		const fetches = sectionEls.map(async (sectionEl): Promise<SectionPayload | null> => {
			const sectionId = sectionEl.getAttribute('id')?.replace('shopify-section-', '')
			if (!sectionId) return null
			const responseHTML = await shopifySectionAPI(`${productUrl}?section_id=${sectionId}`)
			if (sectionEl.querySelector('section[id^="MainProduct-"]')) {
				const productElement = q.os<HTMLElement>('section[id^="MainProduct-"]', responseHTML.documentElement)
				if (!productElement) return null
				return { type: 'main', sectionEl, productElement }
			}
			const html = q.os<HTMLElement>('section.shopify-section', responseHTML.documentElement)
			if (!html) return null
			return { type: 'section', sectionEl, htmlString: html.innerHTML }
		})

		const [, payloadList] = await Promise.all([fadeOut, Promise.all(fetches)])
		const payloads = payloadList.filter((p): p is SectionPayload => !!p)

		for (const p of payloads) {
			if (p.type === 'main') {
				await this.updateProductContent(p.productElement, originalElement, productUrl, false)
				this.cancelScrollTriggers(p.sectionEl)
				
				// Wait for DOM to be fully ready and components to be initialized
				await new Promise(resolve => {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							resolve(undefined)
						})
					})
				})
				
				const newVariantPicker = q.os<ProductVariantPicker>('product-variant-picker', originalElement)
				if (newVariantPicker) {
					newVariantPicker.restoreGenderAndSize(savedGender, savedVariantId, savedSizeDisplayName)
				}
				if (savedDefaultVariantOptions) {
					this.restoreDefaultVariantOptions(originalElement, savedDefaultVariantOptions)
				}
				const newStickyAtc = q.os<ProductStickyAtc>('product-sticky-atc', document)
				if (newStickyAtc && (savedStickyAtcGender || savedStickyAtcVariantId || savedStickyAtcSizeDisplayName)) {
					newStickyAtc.restoreSelections(savedStickyAtcGender, savedStickyAtcVariantId, savedStickyAtcSizeDisplayName)
				}
			} else {
				this.setInnerHTML(p.sectionEl, p.htmlString)
				this.cancelScrollTriggers(p.sectionEl)
			}
		}

		// Clear loading states on all color swatches after content is updated
		const clearLoadingStates = () => {
			const allColorSwatches = document.querySelectorAll('.variant-picker__color-item')
			allColorSwatches.forEach(swatch => {
				const swatchEl = swatch as HTMLButtonElement
				swatchEl.removeAttribute('aria-disabled')
				swatchEl.removeAttribute(ATTRIBUTES.loading)
				const spinner = swatchEl.querySelector(SELECTORS.loadingOverlaySpinner) as HTMLElement | null
				if (spinner) spinner.classList.add('hidden')
			})
		}

		if (!prefersReducedMotion) {
			for (const el of fadeTargets) el.style.opacity = '1'
			await new Promise((resolve) => setTimeout(resolve, DURATION))
			clearLoadingStates()
			for (const el of fadeTargets) {
				const prev = previousStyles.get(el)
				if (!prev) continue
				el.style.transition = prev.transition
				el.style.opacity = prev.opacity
			}
		} else {
			clearLoadingStates()
		}
    }

    /**
     * Populate empty gender-specific size spans from fallback text.
     * After a color change, the new DOM may have empty gender spans
     * that need content from the gender-neutral fallback span.
     */
    private populateEmptyGenderSpans() {
        const sizeLabels = q.ol<HTMLLabelElement>('.size-radios__size-label', this)
        if (!sizeLabels) return

        for (const label of sizeLabels) {
            const genderSpans = q.ol<HTMLElement>('.size-radios__size-text[data-gender]', label)
            const fallbackSpan = q.os<HTMLElement>('.size-radios__size-text:not([data-gender])', label)
            const fallbackText = fallbackSpan?.textContent?.trim()

            if (fallbackText && genderSpans) {
                for (const span of genderSpans) {
                    if (!span.textContent?.trim() && span.dataset.gender) {
                        span.textContent = fallbackText
                    }
                }
            }
        }
    }

    onChangeGender(e: Event) {
        e.preventDefault()
        const selectedGenderInput = q.rs('.size-radios__gender-input:checked', this) as HTMLInputElement
        if(!selectedGenderInput) return

        const selectedGender = selectedGenderInput.value

        this.populateEmptyGenderSpans()

        const sizeTexts = q.ol<HTMLElement>('.size-radios__size-text', this)
        if (sizeTexts) {
            for (const text of sizeTexts) {
                text.classList.toggle('hidden', text.dataset.gender != selectedGender)
            }
        }
    }

    restoreGenderAndSize(savedGender: string | null, savedVariantId: string | null, savedSizeDisplayName: string | null) {
        this.populateEmptyGenderSpans()
        
        if (savedGender) {
            const genderInput = q.os<HTMLInputElement>(`input[name="product-variant-picker-gender"][value="${savedGender}"]`, this)
            if (genderInput) {
                genderInput.checked = true
                // Trigger the gender change handler to update size text visibility
                // Use double RAF to ensure DOM is ready
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.onChangeGender(new Event('change'))
                    })
                })
            }
        }

        if (savedVariantId || savedSizeDisplayName) {
            // Use double requestAnimationFrame to ensure DOM updates from gender change are complete
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                // First try to find by variant ID
                let sizeInput: HTMLInputElement | null = null
                
                if (savedVariantId) {
                    sizeInput = q.os<HTMLInputElement>(`.size-radios__size-input[name="id"][value="${savedVariantId}"]`, this) || null
                }
                
                // If variant ID not found, try to match by display name
                if (!sizeInput && savedSizeDisplayName) {
                    const allSizeInputs = q.ol<HTMLInputElement>('.size-radios__size-input', this)
                    if (allSizeInputs) {
                        for (const input of allSizeInputs) {
                            const label = q.os<HTMLLabelElement>(`label[for="${input.id}"]`, this)
                            if (!label) continue
                            
                            // Check if the size text matches (considering gender-specific text)
                            const sizeTextEls = q.ol<HTMLElement>('.size-radios__size-text', label)
                            if (sizeTextEls) {
                                for (const textEl of sizeTextEls) {
                                    const textContent = textEl.textContent?.trim()
                                    // Match if text matches and it's visible (or if no gender was saved, match any)
                                    const isVisible = !textEl.classList.contains('hidden')
                                    const isMatchingGender = savedGender ? textEl.dataset.gender === savedGender : true
                                    
                                    if (textContent === savedSizeDisplayName && isVisible && isMatchingGender) {
                                        sizeInput = input
                                        break
                                    }
                                }
                            }
                            if (sizeInput) break
                        }
                    }
                }
                
                // Select the size if found
                if (sizeInput) {
                    sizeInput.checked = true
                    // Trigger the size change handler to update form and UI
                    this.onChangeSize(new Event('change'))
                }
                })
            })
        }
    }

    onChangeSize(e: Event) {
        e.preventDefault()
		if (!this.form || !this.formIdEl || !this.submitButton) return

        const selectedSizeInput = q.rs('.size-radios__size-input:checked', this) as HTMLInputElement
        if(!selectedSizeInput) return
        window.history.replaceState({}, '', getUrlWithPreviewThemeId(`${this.dataset.url}?variant=${selectedSizeInput.value}`).toString())

        const resolvedState = resolveStoqVariantState({
            variantId: selectedSizeInput.value,
            available: selectedSizeInput.dataset.available == 'true',
            preorder: selectedSizeInput.dataset.preorder == 'true',
            inventoryStatus: selectedSizeInput.dataset.inventoryStatus || '',
            inventoryStatusOverride:
                selectedSizeInput.dataset.inventoryStatusOverride || '',
            inventoryStatusText: selectedSizeInput.dataset.inventoryStatusText || '',
            liquidSellingPlanId: selectedSizeInput.dataset.sellingPlanId || '',
        })

        this.formIdEl.value = selectedSizeInput.value
        syncResolvedStoqVariantSelection({
            form: this.form,
            submitButton: this.submitButton,
            variantId: selectedSizeInput.value,
            resolvedState,
            fallbackShippingText: selectedSizeInput.dataset.shippingText || '',
            oosPopupUrl: `${this.dataset.url}?variant=${selectedSizeInput.value}&section_id=waitlist`,
        })
		const personalizationBadge = document.querySelector('product-info .price__badge-personalization') as HTMLElement
		if(personalizationBadge) {
			personalizationBadge.dataset.variantId = this.formIdEl.value
		}

		const sizePopupDialogue = SizePopupDialogue.findForSection(this)
		if (selectedSizeInput.dataset.sizePopup === 'true') {
			const sizeLabel = q.rs(`label[for="${selectedSizeInput.id}"]`, this) as HTMLElement
			if (sizePopupDialogue && sizeLabel) {
				sizePopupDialogue.show(sizeLabel)
			}
		} else {
			sizePopupDialogue?.close()
		}

		scheduleSubmitButtonLabelSync(
			this.submitButton,
			() => this.getSubmitButtonState()
		)

		const personalizationTriggers = document.querySelectorAll<HTMLElement>('section[id^="MainProduct-"] .trigger-personalization-popup')
		personalizationTriggers.forEach(trigger => {
			trigger.classList.toggle('disabled', !resolvedState.available)
		})
    }

    override disconnectedCallback(): void {
		if (this.submitButton) {
			clearScheduledSubmitButtonLabelSync(this.submitButton)
		}
		super.disconnectedCallback()
    }

    getSpinner(target: HTMLButtonElement) {
        return q.rs(SELECTORS.loadingOverlaySpinner, target)
    }

    setInnerHTML(element: HTMLElement, html: string) {
		element.innerHTML = html

		// Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
		element.querySelectorAll('script').forEach((oldScriptTag: HTMLScriptElement) => {
			if (!oldScriptTag.parentNode)
				throw new Error('oldScriptTag.parentNode is null, cannot replace script')
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}

	async fadeSwap(
		container: HTMLElement,
		newHTML: string,
		afterReplace?: () => Promise<void> | void,
		durationMs = 160
	) {
		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
		if (prefersReducedMotion) {
			this.setInnerHTML(container, newHTML)
			if (afterReplace) await afterReplace()
			return
		}

		const previousTransition = container.style.transition
		const previousOpacity = container.style.opacity
		container.style.transition = `opacity ${durationMs}ms ease`
		container.style.opacity = '0'
		await new Promise((resolve) => setTimeout(resolve, durationMs))

		this.setInnerHTML(container, newHTML)
		if (afterReplace) await afterReplace()

		container.style.opacity = '1'
		await new Promise((resolve) => setTimeout(resolve, durationMs))
		container.style.transition = previousTransition
		container.style.opacity = previousOpacity
	}

	cancelScrollTriggers(container: HTMLElement) {
		const scrollTriggers = container.querySelectorAll('.scroll-trigger') as NodeListOf<HTMLElement>
		scrollTriggers.forEach((el) => {
			el.classList.add('scroll-trigger--cancel')
			el.classList.remove('animate--slide-in')
			el.classList.remove('animate--fade-in')
			el.style.opacity = '1'
			el.style.transform = ''
			el.style.animation = 'none'
		})
	}

    async updateProductContent(productElement: HTMLElement, originalElement: HTMLElement, productUrl: string, doFade: boolean = true) {
		this.preventDuplicatedIDs(productElement)

		const applyUpdates = async () => {
			this.setInnerHTML(originalElement, productElement.innerHTML)
			this.cancelScrollTriggers(originalElement)

			await Promise.all([
				window.Ucoast.mediaManager.playAllInContainer(originalElement),
				this.initializeProductFeatures()
			])

			this.removeGalleryListSemantic(originalElement)
			this.updateImageSizes(originalElement)
			this.preventVariantURLSwitching(originalElement)

			window.history.replaceState({}, '', getUrlWithPreviewThemeId(productUrl).toString())

			const productModals = document.querySelectorAll('product-modal')
			productModals.forEach((modal, index) => {
				if(index < productModals.length - 1) modal.remove()
			})
		}

		if (doFade) {
			await this.fadeSwap(originalElement, productElement.innerHTML, applyUpdates)
		} else {
			await applyUpdates()
		}
    }

    async initializeProductFeatures() {
        if (window.Shopify?.PaymentButton) {
            window.Shopify.PaymentButton.init()
        }
        if (window.ProductModel) {
            window.ProductModel.loadShopifyXR()
        }
    }

    preventVariantURLSwitching(originalElement: HTMLElement) {
		const variantPicker = originalElement.querySelector('variant-radios,variant-selects')
		if (!variantPicker) return

		variantPicker.setAttribute('data-update-url', 'false')
	}

	preventDuplicatedIDs(productElement: HTMLElement) {
		if (!productElement)
			throw new Error('this.preventDuplicatedIDs called without productElement')
		const sectionId = q.ra(productElement, 'data-section')
		productElement.innerHTML = replaceAll(
			productElement.innerHTML,
			sectionId,
			`quickadd-${sectionId}`
		)
		const formComponent = q.rs<ProductInfo>(
			'product-info',
			productElement
		)
        if(formComponent) formComponent.dataset.originalSection = sectionId
	}

	removeGalleryListSemantic(originalElement: HTMLElement) {
		const galleryList = originalElement.querySelector('[id^="Slider-Gallery"]')
		if (!galleryList) return

		if(originalElement.classList.contains('quick-add-modal__content-info')) galleryList.classList.remove('slider')
		galleryList.setAttribute('role', 'presentation')
		galleryList
			.querySelectorAll('[id^="Slide-"]')
			.forEach((li) => li.setAttribute('role', 'presentation'))
	}

	updateImageSizes(originalElement: HTMLElement) {
		const product = q.rs('.product', originalElement)
		const desktopColumns = product.classList.contains('product--columns')
		if (!desktopColumns) return

		const mediaImages = q.ol<HTMLImageElement>('.product__media img', product)
		if (!mediaImages) return

		let mediaImageSizes =
			'(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)'

		if (product.classList.contains('product--medium')) {
			mediaImageSizes = mediaImageSizes.replace('715px', '605px')
		} else if (product.classList.contains('product--small')) {
			mediaImageSizes = mediaImageSizes.replace('715px', '495px')
		}

		mediaImages.forEach((img: HTMLImageElement) => img.setAttribute('sizes', mediaImageSizes))
	}

	/**
	 * Saves selected options from default variant pickers (variant-radios/variant-selects)
	 * Returns array of selected option values with their indices, skipping the color option
	 * Format: [{ index: number, value: string, optionName: string }]
	 */
	saveDefaultVariantOptions(container: HTMLElement): Array<{ index: number; value: string; optionName: string }> | null {
		const variantRadios = q.os<VariantRadios>('variant-radios', container)
		const variantSelects = q.os<VariantSelects>('variant-selects', container)
		
		if (!variantRadios && !variantSelects) return null
		
		const savedOptions: Array<{ index: number; value: string; optionName: string }> = []
		
		if (variantRadios) {
			// For variant-radios: get checked inputs from each fieldset
			const fieldsets = q.rl<HTMLFieldSetElement>('fieldset', variantRadios)
			if (fieldsets && fieldsets.length > 1) {
				for (let i = 0; i < fieldsets.length; i++) {
					const legend = q.os<HTMLLegendElement>('legend', fieldsets[i])
					const optionName = legend?.textContent?.trim().toLowerCase() || ''
					// Skip color option (can be in any position)
					if (optionName.includes('color') || optionName.includes('colour')) continue
					
					const checkedInput = q.os<HTMLInputElement>('input[type="radio"]:checked', fieldsets[i])
					if (checkedInput) {
						savedOptions.push({ index: i, value: checkedInput.value, optionName })
					}
				}
			}
		} else if (variantSelects) {
			// For variant-selects: get selected values from each select
			const selects = q.rl<HTMLSelectElement>('select', variantSelects)
			if (selects && selects.length > 1) {
				for (let i = 0; i < selects.length; i++) {
					const label = q.os<HTMLLabelElement>(`label[for="${selects[i].id}"]`, variantSelects)
					const optionName = label?.textContent?.trim().toLowerCase() || ''
					// Skip color option (can be in any position)
					if (optionName.includes('color') || optionName.includes('colour')) continue
					
					if (selects[i].value) {
						savedOptions.push({ index: i, value: selects[i].value, optionName })
					}
				}
			}
		}
		
		return savedOptions.length > 0 ? savedOptions : null
	}

	/**
	 * Restores selected options for default variant pickers (variant-radios/variant-selects)
	 */
	restoreDefaultVariantOptions(container: HTMLElement, savedOptions: Array<{ index: number; value: string; optionName: string }>): void {
		if (!savedOptions || savedOptions.length === 0) return
		
		const variantRadios = q.os<VariantRadios>('variant-radios', container)
		const variantSelects = q.os<VariantSelects>('variant-selects', container)
		
		if (!variantRadios && !variantSelects) return
		
		// Use requestAnimationFrame to ensure DOM is ready and variant picker is initialized
		requestAnimationFrame(() => {
			if (variantRadios) {
				// For variant-radios: restore checked inputs by matching option name or index
				const fieldsets = q.rl<HTMLFieldSetElement>('fieldset', variantRadios)
				if (fieldsets && fieldsets.length > 0) {
					for (const savedOption of savedOptions) {
						// Try to find fieldset by matching option name first
						let targetFieldset: HTMLFieldSetElement | null = null
						for (const fieldset of fieldsets) {
							const legend = q.os<HTMLLegendElement>('legend', fieldset)
							const optionName = legend?.textContent?.trim().toLowerCase() || ''
							if (optionName === savedOption.optionName || fieldset === fieldsets[savedOption.index]) {
								targetFieldset = fieldset
								break
							}
						}
						
						if (targetFieldset) {
							const inputToCheck = q.os<HTMLInputElement>(`input[type="radio"][value="${savedOption.value}"]:not(.disabled)`, targetFieldset)
							if (inputToCheck) {
								inputToCheck.checked = true
								inputToCheck.dispatchEvent(new Event('change', { bubbles: true }))
							}
						}
					}
				}
			} else if (variantSelects) {
				// For variant-selects: restore selected values by matching option name or index
				const selects = q.rl<HTMLSelectElement>('select', variantSelects)
				if (selects && selects.length > 0) {
					for (const savedOption of savedOptions) {
						// Try to find select by matching option name first
						let targetSelect: HTMLSelectElement | null = null
						for (const select of selects) {
							const label = q.os<HTMLLabelElement>(`label[for="${select.id}"]`, variantSelects)
							const optionName = label?.textContent?.trim().toLowerCase() || ''
							if (optionName === savedOption.optionName || select === selects[savedOption.index]) {
								targetSelect = select
								break
							}
						}
						
						if (targetSelect) {
							const option = q.os<HTMLOptionElement>(`option[value="${savedOption.value}"]:not([disabled])`, targetSelect)
							if (option) {
								targetSelect.value = savedOption.value
								targetSelect.dispatchEvent(new Event('change', { bubbles: true }))
							}
						}
					}
				}
			}
		})
	}
}
